import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Constants } from './constants';
import { Utils } from './utils';
import { LocalStorage } from './local-storage';
import { Tags } from './tags';
import { Toast } from './toast';
import { Route } from './route';
import { environment } from 'src/environments/environment';
import { GUI } from './gui';
import { LoaderService } from './loader-service';
import { MemoryStorage } from './memory-storage';

@Injectable({
    providedIn: 'root'
})
export class HttpUtils {

    constructor(private gui: GUI, private httpClient: HttpClient, private browserStorageService: MemoryStorage, private constants: Constants, private utils: Utils, private localStorage: LocalStorage, private tags: Tags, private toast: Toast, private route: Route, private loaderService: LoaderService) {
    }

    private fetchRequestHeaders(authorizationWithKey: boolean) {
        let applicationToken = this.constants.BLANK;

        if (authorizationWithKey === true) {
            applicationToken = this.utils.encryptString(environment.authorizationKey);
        } else {
            applicationToken = this.browserStorageService.get(this.tags.APPLICATION_TOKEN);
        }

        const encryptedAuthorizationWithKey = this.utils.encryptString(authorizationWithKey.toString())

        let headers = new HttpHeaders();
        headers = headers.append(this.tags.AUTHORIZATION, encryptedAuthorizationWithKey);
        headers = headers.append(this.tags.SERVER_APPLICATION_TOKEN, applicationToken);

        const keycloakToken = this.browserStorageService.get(this.tags.KEYCLOAK_TOKEN);
        if (!this.utils.isEmpty(keycloakToken)) {
            headers = headers.append('Authorization', 'Bearer ' + keycloakToken);
        }

        return headers;
    }

    private async sendPostRequest(url: string, requestBody: {}, authorizationWithKey: boolean) {
        url = environment.serverBaseUrl + url;

        const headers = this.fetchRequestHeaders(authorizationWithKey);

        return this.httpClient.post(url, requestBody, { headers }).toPromise()
            .then(response => {
                if (response[this.tags.RESPONSE_CODE] === this.constants.STATUS_AUTH_TOKEN_NOT_VALID) {
                    this.loaderService.stopAllLoader();
                    const errorMessage = response[this.tags.RESPONSE_MESSAGE];
                    this.toast.showErrorToastWithCallbackFunction(errorMessage, this.openLogoutPage.bind(null, this.gui));
                    return;
                }
                return response;
            }).catch(error => {
                console.log('Server error come', error);
                return this.fetchErrorObject(error.message);
            });
    }


    private async sendRequest(
        url: string,
        method: string,
        requestBody: {} = {}
    ): Promise<any> {
        url = environment.hashBaseUrl + url;

        try {
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                },
                body: method === 'GET' ? null : JSON.stringify(requestBody), // Only attach body for non-GET requests
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Server error');
            }

            return await response.json();
        } catch (error) {
            console.error('Server error occurred:', error);
            return this.fetchErrorObject(error.message);
        }
    }



    private openLogoutPage(gui: GUI): void {
        gui.openLogoutPage();
    }

    private fetchErrorObject(errorMessage: string): any {
        const errorResponseObject: any = { [this.tags.RESPONSE_CODE]: this.constants.INTERNAL_SERVER_ERROR, [this.tags.RESPONSE_MESSAGE]: errorMessage };
        return errorResponseObject;
    }

    async fetchServerToken() {
        const requestBody = {};

        return await this.sendPostRequest('auth/serverToken', requestBody, true);
    }

    async saveLoginDetails(id: string, email: string, username: string, firstName: string, lastName: string) {
        const requestBody = { id: id, email: email, username: username, firstName: firstName, lastName: lastName };

        return await this.sendPostRequest('user/saveUser', requestBody, false);
    }

    async fetchDashboardData(loggedUserId: string) {
        const requestBody = { loggedUserId: loggedUserId };

        return await this.sendPostRequest('dashboard/fetchDashboardData', requestBody, false);
    }

    async fetchCountriesList() {
        const requestBody = {};

        return await this.sendPostRequest('config/fetchAllCountries', requestBody, false);
    }

    async fetchVisaClasses(countryCode: string) {
        const requestBody = { nationality: countryCode };

        return await this.sendPostRequest('config/fetchVisaClasses', requestBody, false);
    }

    async fetchPassports(nationality: string) {
        const requestBody = { nationality };

        return await this.sendPostRequest('config/fetchPassports', requestBody, false);
    }

    async fetchCountryStateList() {
        const requestBody = {};

        return await this.sendPostRequest('config/fetchCountryStates', requestBody, false);
    }

    async fetchVisaFeeDetails(formData: {}) {
        const requestBody = formData;

        return await this.sendPostRequest('config/fetchVisaFee', requestBody, false);
    }

    async saveGeneralAndBioDetails(personalDetailsFormObject: {}, generalDetailsFormObject: {}) {
        const requestBody = Object.assign(personalDetailsFormObject, generalDetailsFormObject);

        return await this.sendPostRequest('application/saveGeneralAndBioDetails', requestBody, false);
    }

    async saveTravelDetails(travelDetailsFormObject: {}) {
        const requestBody = travelDetailsFormObject;

        return await this.sendPostRequest('application/saveTravelDetails', requestBody, false);
    }

    async saveContactDetails(contactDetailsFormObject: {}) {
        const requestBody = contactDetailsFormObject;

        return await this.sendPostRequest('application/saveContactDetails', requestBody, false);
    }

    async updateGeneralDetails(generalDetailsFormObject: {}) {
        const requestBody = generalDetailsFormObject;

        return await this.sendPostRequest('application/updateGeneralDetails', requestBody, false);
    }

    async fetchApplicationData(applicantId: string, loggedUserId: string) {
        const requestBody = { applicantId, loggedUserId };

        return await this.sendPostRequest('application/fetchData', requestBody, true);
    }

    async confirmApplication(loggedUserId: string, applicantId: string, appId: string) {
        const requestBody = { loggedUserId: loggedUserId, applicantId: applicantId, appId: appId };

        return await this.sendPostRequest('application/confirmApplication', requestBody, false);
    }

    async deleteApplication(loggedUserId: string, applicantId: string, isDeleteIncomplete: number) {
        const requestBody = { loggedUserId: loggedUserId, applicantId: applicantId, isDeleteIncomplete: isDeleteIncomplete };

        return await this.sendPostRequest("application/deleteApplication", requestBody, false);
    }

    async searchApplication(formData: {}) {
        const requestBody = formData;

        return await this.sendPostRequest('application/searchApplication', requestBody, false);
    }

    async saveApplicationToCart(loggedUserId: string, applicantId: string) {
        const requestBody = { loggedUserId: loggedUserId, applicantId: applicantId };

        return await this.sendPostRequest("application/addApplicationToCart", requestBody, false);
    }

    async fetchV1ApplicationData(applicationIdV1: string, referenceIdV1: string) {
        const requestBody = { applicationId: applicationIdV1, referenceId: referenceIdV1 };

        return await this.sendPostRequest('application/fetchV1ApplicationData', requestBody, false);
    }

    async initiatePayment(loggedUserId: string, applicationsList: any[]) {
        const authToken = this.browserStorageService.get(this.tags.APPLICATION_TOKEN);

        const requestBody = { authToken: authToken, loggedUserId: loggedUserId, applicationsList: applicationsList };

        return await this.sendPostRequest("payment/initiatePayment", requestBody, false);
    }

    async fetchUserPaidApplications(loggedUserId: string, data: string) {
        const requestBody = { loggedUserId, data };

        return await this.sendPostRequest('payment/fetchUserPaidApplications', requestBody, false);
    }

    async checkPaymentStatusOnIOS(loggedUserId: string, applicantId: string, isNeedApplicationsList: boolean) {
        const requestBody = { loggedUserId: loggedUserId, applicantId: applicantId, isNeedApplicationsList: isNeedApplicationsList };

        return await this.sendPostRequest("payment/checkPaymentStatusOnIOS", requestBody, false);
    }

    async fetchFeeAndPaymentDataFromIOS(applicationId: string, referenceId: string) {
        const requestBody = { applicationId: applicationId, referenceId: referenceId };

        return await this.sendPostRequest("payment/fetchFeeAndPaymentDataFromIOS", requestBody, false);
    }

    async fetchSupportingDocumentsList(visaDetail: {}) {
        const requestBody = visaDetail;

        return await this.sendPostRequest("config/fetchSupportingDocumentsList", requestBody, false);
    }

    async saveSupportingDocuments(supportingDocumentsFormObject: {}) {
        const requestBody = supportingDocumentsFormObject;

        return await this.sendPostRequest("application/saveSupportingDocuments", requestBody, false);
    }


    async saveTravelHistory(travelHistoryFormObject: {}) {
        const requestBody = travelHistoryFormObject;
        return await this.sendPostRequest("application/saveTravelHistory", requestBody, false);
    }

    async saveSecurityHistory(securityHistoryFormObject: {}) {
        const requestBody = securityHistoryFormObject;
        return await this.sendPostRequest("application/saveSecurityHistory", requestBody, false);
    }

    async saveBiometricInfo(biometricFormObject: {}) {
        const requestBody = biometricFormObject;
        return await this.sendPostRequest("application/saveBiometricInfo", requestBody, false);
    }

    async fetchPaymentReceiptData(applicantId: string, isV1Application: number) {
        const requestBody = { applicantId, isV1Application };

        return await this.sendPostRequest("application/fetchPaymentReceiptData", requestBody, false);
    }


    async startRavePayment(payload: {}) {
        const requestBody = payload;
        return await this.sendPostRequest(
            "payment/initiateRavePayment",
            requestBody,
            false
        );
    }


    async getHash(payload: {}) {
        const requestBody = payload;
        return await this.sendRequest(
            "v2/get-signature",
            "POST",
            requestBody,

        );
    }


    async fetchApplicationPaymentDetailsV2(applicantId: string, paymentType: string, contactlessEligibility?: string) {
        const requestBody = {};


        let url = `v1/payments/allPaymentGatewaysV2/${applicantId}/${paymentType}`;

        if (contactlessEligibility) {
            url += `?contactlessEligibility=${encodeURIComponent(contactlessEligibility)}`;
        }

        return await this.sendPostRequest(url, requestBody, false);
    }


    async makeApplicationPayment(requestBody: any = {}) {

        return await this.sendPostRequest('payment/initiatePayment', requestBody, false);
    }

    async verifyPayment(origTxnRef: string) {
        const requestBody = { origTxnRef };

        return await this.sendPostRequest(
            'payment/confirmPaymentV2',
            requestBody,
            false
        );
    }


    async fetchAllPaymentGateway() {
        // const requestBody = {applicationId, loggedUserId}; 

        return await this.sendPostRequest("payment/allPaymentGatewaysV2", null, false);
    }

    async confirmApplicationV2(loggedUserId: string, applicantId: string, applicantIds: any) {
        const requestBody = { loggedUserId: loggedUserId, applicantId: applicantId, applicantIds: applicantIds };

        return await this.sendPostRequest('application/confirmApplication', requestBody, false);
    }

}
